/**
 * Wallet Network Graph — compact relationship visualization for Wallet Intelligence.
 * The scanned wallet is always at the center, visually distinguished.
 * Clicking any connected wallet updates the info panel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { WalletIntelligenceData } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];
const VIEW_W = 800;
const VIEW_H = 400;

const TYPE_COLORS: Record<string, string> = {
  funding: '#f0b90b',
  frequent: '#a78bfa',
  'cluster-member': '#14e0b0',
  counterparty: '#fb923c',
};

const TYPE_LABELS: Record<string, string> = {
  funding: 'Funding',
  frequent: 'Frequent',
  'cluster-member': 'Cluster',
  counterparty: 'Counterparty',
};

interface WalletNetworkGraphProps {
  data: WalletIntelligenceData;
  onSelectWallet: (address: string) => void;
  selectedAddress: string;
}

interface Positions {
  [id: string]: { x: number; y: number };
}

function computeLayout(nodes: WalletIntelligenceData['network']['nodes'], centerId: string): Positions {
  const positions: Positions = {};
  const cx = VIEW_W / 2;
  const cy = VIEW_H / 2;

  positions[centerId] = { x: cx, y: cy };

  const outer = nodes.filter((n) => n.id !== centerId);
  const radius = Math.min(VIEW_W, VIEW_H) * 0.34;
  outer.forEach((node, i) => {
    const angle = (i / Math.max(outer.length, 1)) * Math.PI * 2 - Math.PI / 2;
    positions[node.id] = {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  });

  return positions;
}

export default function WalletNetworkGraph({ data, onSelectWallet, selectedAddress }: WalletNetworkGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const positions = useMemo<Positions>(() => computeLayout(data.network.nodes, data.address), [data.network.nodes, data.address]);

  const centerNode = data.network.nodes.find((n) => n.id === data.address);
  const otherNodes = data.network.nodes.filter((n) => n.id !== data.address);

  const handleNodeClick = useCallback(
    (nodeId: string) => (e: React.PointerEvent) => {
      e.stopPropagation();
      onSelectWallet(nodeId);
    },
    [onSelectWallet],
  );

  return (
    <div className="relative h-[400px] w-full overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.15]" />

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="h-full w-full select-none"
      >
        <defs>
          <filter id="glow-center" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Edges */}
        {data.network.edges.map((edge, i) => {
          const s = positions[edge.source];
          const t = positions[edge.target];
          if (!s || !t) return null;
          const isHighlighted =
            selectedAddress === edge.source ||
            selectedAddress === edge.target ||
            hoveredId === edge.source ||
            hoveredId === edge.target;

          return (
            <motion.line
              key={`${edge.source}-${edge.target}`}
              x1={s.x}
              y1={s.y}
              x2={t.x}
              y2={t.y}
              stroke={isHighlighted ? '#ffffff' : '#a78bfa'}
              strokeWidth={isHighlighted ? 2 : 1}
              strokeOpacity={isHighlighted ? 0.8 : 0.25}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: isHighlighted ? 0.8 : 0.25 }}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.08, ease: easeOut }}
            />
          );
        })}

        {/* Other nodes */}
        {otherNodes.map((node, i) => {
          const pos = positions[node.id];
          if (!pos) return null;
          const color = TYPE_COLORS[node.type] ?? '#a1a1aa';
          const isSelected = selectedAddress === node.id;
          const isHovered = hoveredId === node.id;
          const isDimmed =
            selectedAddress && selectedAddress !== data.address && selectedAddress !== node.id;

          return (
            <g
              key={node.id}
              transform={`translate(${pos.x} ${pos.y})`}
              onPointerDown={handleNodeClick(node.id)}
              onMouseEnter={() => setHoveredId(node.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="cursor-pointer"
              style={{ opacity: isDimmed ? 0.3 : 1, transition: 'opacity 0.2s' }}
            >
              <motion.g
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{
                  opacity: 1,
                  scale: isSelected || isHovered ? 1.3 : 1,
                }}
                transition={{ duration: 0.5, delay: 0.05 * i, ease: easeOut }}
              >
                {(isSelected || isHovered) && (
                  <circle r={14} fill={color} style={{ filter: 'blur(6px)' }} opacity={0.4} />
                )}
                <circle
                  r={isSelected ? 10 : 7}
                  fill={color}
                  stroke={isSelected ? '#ffffff' : color}
                  strokeWidth={isSelected ? 2 : 1}
                  strokeOpacity={isSelected ? 0.95 : 0.5}
                  style={{ transition: 'all 0.2s ease' }}
                />
                <text
                  y={20}
                  textAnchor="middle"
                  fontSize={9.5}
                  fontWeight={600}
                  fill="rgba(255,255,255,0.85)"
                  fontFamily="'Hanken Grotesk', sans-serif"
                >
                  {node.label}
                </text>
                <text
                  y={32}
                  textAnchor="middle"
                  fontSize={8}
                  fontWeight={500}
                  fill={color}
                  fontFamily="'Hanken Grotesk', sans-serif"
                >
                  {TYPE_LABELS[node.type] ?? node.type}
                </text>
              </motion.g>
            </g>
          );
        })}

        {/* Center node (scanned wallet) — rendered last so it's on top */}
        {centerNode && (() => {
          const pos = positions[centerNode.id];
          if (!pos) return null;
          return (
            <g
              key={centerNode.id}
              transform={`translate(${pos.x} ${pos.y})`}
              onPointerDown={handleNodeClick(centerNode.id)}
              onMouseEnter={() => setHoveredId(centerNode.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="cursor-pointer"
            >
              <motion.g
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: easeOut }}
              >
                {/* Glow ring */}
                <circle
                  r={22}
                  fill="none"
                  stroke="#14e0b0"
                  strokeWidth={1.5}
                  strokeOpacity={0.3}
                  style={{ filter: 'drop-shadow(0 0 8px #14e0b0)' }}
                />
                <circle
                  r={16}
                  fill="#14e0b0"
                  style={{ filter: 'blur(6px)' }}
                  opacity={0.25}
                />
                <circle
                  r={12}
                  fill="#14e0b0"
                  stroke="#ffffff"
                  strokeWidth={2}
                  strokeOpacity={0.9}
                />
                <text
                  y={28}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill="#14e0b0"
                  fontFamily="'Hanken Grotesk', sans-serif"
                >
                  {centerNode.label}
                </text>
                <text
                  y={40}
                  textAnchor="middle"
                  fontSize={8}
                  fontWeight={600}
                  fill="rgba(255,255,255,0.6)"
                  fontFamily="'Hanken Grotesk', sans-serif"
                >
                  Scanned Wallet
                </text>
              </motion.g>
            </g>
          );
        })()}
      </svg>

      {/* Legend */}
      <div className="pointer-events-none absolute bottom-3 left-3 flex flex-wrap gap-2">
        {Object.entries(TYPE_LABELS).map(([type, label]) => (
          <span key={type} className="inline-flex items-center gap-1 text-[9px] font-medium text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: TYPE_COLORS[type] }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
